import React, { createContext, useContext } from "react";
import { useRouter } from "next/router";
import { searchActions } from "@/store/actions";
import { DEFAULT_LANGUAGE } from "@/shared/utils/multilang/constants";
import { getSearchSuggestionsShortcutRequest } from "@/shared/api/search/suggestions/requests";
import {
  handleSearchShortcut,
  isQueryOnlyText,
  setShortcutDataParamsToSearchParams
} from "@/shared/utils/search/search";
import { getBookIdNumber } from "@/shared/utils/content/content";
import { getShopBooksRequest } from "@/shared/api/shopApi";
import { makeBookUrl, makeChapterUrl } from "@/utils/routes/routes";
import { NEXT_URLS } from "@/utils/routes/routes.constants";
import {
  basicSearchDefault,
  makeSearchUrlObject
} from "@/components/SearchWidget/SearchWidget.utils";
import { useTranslation } from "next-i18next";
import { useDispatch, useSelector } from "react-redux";
import { selectorSetting } from "@/shared/store/settings/selectors";
import { SETTING } from "@/shared/utils/settings/constants";
import { getBiblePubnr } from "@/shared/utils/search/advanced/advanced";

const SearchContext = createContext({});

export const SearchProvider = ({ children }) => {
  const router = useRouter();
  const { i18n } = useTranslation();

  const dispatch = useDispatch();

  const baseBible = useSelector(selectorSetting(SETTING.baseBible.id));

  const resetSearch = () => {
    dispatch(searchActions.reset());
  };

  const searchByShortcut = (shortcut, searchParams) => {
    handleSearchShortcut(shortcut, {
      onShortcutBook: () => {
        resetSearch();

        openBookOrPara(shortcut.bookId);
      },
      onShortcutPara: () => {
        resetSearch();

        openBookOrPara(shortcut.paraId);
      },
      onSearchInBookOrChapter: () => {
        setShortcutDataParamsToSearchParams(
          searchParams,
          shortcut,
          baseBible,
          true,
          false,
        );
        if (searchParams.extras.period) {
          searchParams.period = searchParams.extras.period;
          delete searchParams.extras.period;
        }
        if (shortcut.book_code) {
          // if search in book, not in chapter
          // should be removed, is used inside book code input
          searchParams.bookCode = shortcut.book_code;
        }

        openSearchPage(searchParams);
      },
      onTextSearch: () => {
        openSearchPage(searchParams);
      },
    });
  };

  const openBookOrPara = async (paraId) => {
    const bookId = getBookIdNumber(paraId);
    const shopBooks = await getShopBooksRequest([bookId]);
    const shopBook = shopBooks.books[0];

    if (shopBook && !shopBook.is_purchased) {
      router.push({
        pathname: makeBookUrl(bookId),
      });
      return;
    }

    router.push({
      pathname: makeChapterUrl(paraId),
    });
  };

  const openSearchPage = (searchParams) => {
    router.push({
      pathname: NEXT_URLS.search,
      query: makeSearchUrlObject({
        ...basicSearchDefault,
        ...searchParams,
      }),
    });
  };

  const search = async (query, lang) => {
    const langFinal = lang || router.query.lang || i18n.language || DEFAULT_LANGUAGE;
    const shortcutResponse = await getSearchSuggestionsShortcutRequest(
      query,
      getBiblePubnr(baseBible),
    );
    const newSearchParams = { query, queryFull: query, lang: langFinal };

    if (!isQueryOnlyText(query)) {
      searchByShortcut(shortcutResponse, newSearchParams);
    } else {
      openSearchPage(newSearchParams);
    }
  };

  return (
    <SearchContext.Provider value={{
      search,
      searchByShortcut,
    }}>
      {children}
    </SearchContext.Provider>
  );
};

export const useSearchContext = () => useContext(SearchContext);
